import React, { useMemo, useState } from 'react';
import { FileText, ChevronDown, ChevronUp, ExternalLink, FolderOpen } from 'lucide-react';
import {
    reportTypeLabel,
    subjectFromMetadata,
    subjectIdentifier,
} from '../../utils/reportArtifacts';

export interface SavedInvestigationReport {
    id: string | number;
    title: string;
    report_type: string;
    metadata?: Record<string, any>;
    created_at?: string;
}

export interface AnalysisCaseReportsPanelProps {
    reports: SavedInvestigationReport[];
    onOpenReport?: (artifactId: string) => void | Promise<void>;
}

function formatWhen(iso?: string): string {
    if (!iso) return '';
    const ms = Date.parse(iso);
    if (!Number.isFinite(ms)) return '';
    return new Date(ms).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Case-file evidence list for investigation HTML reports.
 * Archives live in investigation_reports; this panel reopens them from Analysis.
 */
export const AnalysisCaseReportsPanel: React.FC<AnalysisCaseReportsPanelProps> = ({
    reports,
    onOpenReport,
}) => {
    const [expanded, setExpanded] = useState(true);
    const [openingId, setOpeningId] = useState<string | null>(null);

    const sorted = useMemo(() => {
        return [...reports].sort((a, b) => {
            const ad = a.created_at ? Date.parse(a.created_at) : 0;
            const bd = b.created_at ? Date.parse(b.created_at) : 0;
            return bd - ad;
        });
    }, [reports]);

    const handleOpen = async (id: string | number) => {
        if (!onOpenReport) return;
        const key = String(id);
        setOpeningId(key);
        try {
            await onOpenReport(key);
        } finally {
            setOpeningId(null);
        }
    };

    return (
        <div
            className="flex flex-col rounded-xl border border-border/80 bg-card shadow-sm relative z-0 overflow-hidden transition-all duration-300 flex-none"
            data-testid="analysis-case-reports"
            data-tour="analysis-case-reports"
        >
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className={`flex items-center justify-between w-full p-3 font-semibold text-sm hover:bg-muted/50 transition-colors ${expanded ? 'bg-secondary/30 border-b border-border/50' : ''}`}
            >
                <div className="flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-primary" />
                    Case Investigation Reports
                    {sorted.length > 0 && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary/15 text-foreground border border-primary/30">
                            {sorted.length}
                        </span>
                    )}
                </div>
                {expanded
                    ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                    : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </button>

            {expanded && (
                <div className="px-3 py-3 space-y-2">
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                        Immutable printable reports stored on this investigation (case file).
                        Targeted Node Intelligence, Follow the Money, Holistic Multi-Node Trace,
                        and AI Graph Summary each archive as a separate file you can open and
                        Print / Save as PDF.
                    </p>

                    {sorted.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-border/80 bg-muted/20 px-3 py-4 text-center">
                            <FileText className="w-5 h-5 text-muted-foreground mx-auto mb-1.5 opacity-70" />
                            <p className="text-xs text-muted-foreground">
                                No archived reports yet. Complete an investigation algorithm —
                                reports are saved to the case automatically when the run finishes.
                            </p>
                        </div>
                    ) : (
                        <ul className="space-y-1.5" role="list">
                            {sorted.map((r) => {
                                const id = String(r.id);
                                const shortId = subjectIdentifier(subjectFromMetadata(r.metadata));
                                const fullSubject =
                                    typeof r.metadata?.address === 'string'
                                        ? r.metadata.address
                                        : Array.isArray(r.metadata?.addresses)
                                            ? (r.metadata.addresses as string[]).join(', ')
                                            : undefined;
                                return (
                                    <li
                                        key={id}
                                        className="flex items-start gap-2 rounded-lg border border-border/60 bg-background/60 px-2.5 py-2"
                                    >
                                        <FileText className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                                        <div className="min-w-0 flex-1">
                                            <div className="text-xs font-medium text-foreground truncate" title={r.title}>
                                                {r.title}
                                            </div>
                                            <div className="text-[10px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                                                <span className="font-medium text-foreground/70">
                                                    {reportTypeLabel(r.report_type)}
                                                </span>
                                                {shortId && (
                                                    <span className="font-mono" title={fullSubject}>
                                                        {shortId}
                                                    </span>
                                                )}
                                                {r.created_at && <span>{formatWhen(r.created_at)}</span>}
                                            </div>
                                        </div>
                                        {onOpenReport && (
                                            <button
                                                type="button"
                                                onClick={() => void handleOpen(r.id)}
                                                disabled={openingId === id}
                                                className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-primary/10 text-foreground border border-primary/25 hover:bg-primary/20 transition-colors disabled:opacity-50"
                                                title="Open archived report for evidence"
                                            >
                                                <ExternalLink className="w-3 h-3" />
                                                {openingId === id ? 'Opening…' : 'View'}
                                            </button>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};
